/**
 * A small XML reader, enough for MusicXML and nothing more.
 *
 * The package carries no dependencies, and this is the one place that would otherwise need
 * one: `DOMParser` exists in a browser and not in Node, and the editor's document layer is
 * meant to work in both. What MusicXML actually uses is a narrow slice of XML -- elements,
 * attributes, text, a `DOCTYPE` line, the five named entities and numeric references -- so
 * that slice is read here and the rest is skipped rather than half-supported.
 *
 * Parsing is iterative, with an explicit stack. A recursive reader blows the call stack on
 * a long part, which is exactly the file an editor is asked to open.
 */

/** An element, with its attributes, its element children and its own text. */
export interface XmlElement {
  readonly name: string;
  readonly attrs: Readonly<Record<string, string>>;
  readonly children: readonly XmlElement[];
  /** Text directly inside this element, with entities resolved. */
  readonly text: string;
}

/** A document that could not be read, with where in the source it went wrong. */
export class XmlError extends Error {
  constructor(
    message: string,
    readonly offset: number,
  ) {
    super(`${message} at offset ${offset}`);
    this.name = "XmlError";
  }
}

interface Building {
  name: string;
  attrs: Record<string, string>;
  children: XmlElement[];
  text: string;
}

const NAMED: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};

const ENTITY = /&(#[xX][0-9a-fA-F]+|#[0-9]+|[A-Za-z_][\w.-]*);/g;
const NAME_CHARS = /[A-Za-z0-9_:.-]/;

/** Resolve the entities MusicXML can contain. An unknown one is left as written. */
export function decodeEntities(raw: string): string {
  if (!raw.includes("&")) return raw;
  return raw.replace(ENTITY, (whole: string, body: string) => {
    if (body.startsWith("#x") || body.startsWith("#X")) return fromCode(body.slice(2), 16, whole);
    if (body.startsWith("#")) return fromCode(body.slice(1), 10, whole);
    return NAMED[body] ?? whole;
  });
}

function fromCode(digits: string, radix: number, whole: string): string {
  const code = Number.parseInt(digits, radix);
  return Number.isFinite(code) && code > 0 ? String.fromCodePoint(code) : whole;
}

function finish(node: Building): XmlElement {
  return { name: node.name, attrs: node.attrs, children: node.children, text: node.text.trim() };
}

/** Read a document and return its root element. Throws {@link XmlError} on malformed input. */
export function parseXml(source: string): XmlElement {
  const stack: Building[] = [];
  let root: XmlElement | null = null;
  let at = 0;

  while (at < source.length) {
    if (source[at] !== "<") {
      const next = source.indexOf("<", at);
      const end = next === -1 ? source.length : next;
      const top = stack[stack.length - 1];
      if (top !== undefined) top.text += decodeEntities(source.slice(at, end));
      at = end;
      continue;
    }

    if (source.startsWith("<!--", at)) {
      at = skipTo(source, at, "-->", "unterminated comment");
      continue;
    }
    if (source.startsWith("<![CDATA[", at)) {
      const end = source.indexOf("]]>", at);
      if (end === -1) throw new XmlError("unterminated CDATA", at);
      const top = stack[stack.length - 1];
      if (top !== undefined) top.text += source.slice(at + 9, end);
      at = end + 3;
      continue;
    }
    if (source.startsWith("<?", at)) {
      at = skipTo(source, at, "?>", "unterminated processing instruction");
      continue;
    }
    if (source.startsWith("<!", at)) {
      at = skipDeclaration(source, at);
      continue;
    }

    if (source.startsWith("</", at)) {
      const end = source.indexOf(">", at);
      if (end === -1) throw new XmlError("unterminated closing tag", at);
      const closed = stack.pop();
      if (closed === undefined) throw new XmlError("closing tag with nothing open", at);
      const element = finish(closed);
      const parent = stack[stack.length - 1];
      if (parent === undefined) root = element;
      else parent.children.push(element);
      at = end + 1;
      continue;
    }

    at = openTag(source, at, stack, (element) => {
      const parent = stack[stack.length - 1];
      if (parent === undefined) root = element;
      else parent.children.push(element);
    });
  }

  if (stack.length > 0) throw new XmlError(`unclosed <${stack[stack.length - 1]?.name}>`, at);
  if (root === null) throw new XmlError("no root element", 0);
  return root;
}

function skipTo(source: string, at: number, close: string, complaint: string): number {
  const end = source.indexOf(close, at);
  if (end === -1) throw new XmlError(complaint, at);
  return end + close.length;
}

/** Skip `<!DOCTYPE ...>`, including an internal subset in square brackets. */
function skipDeclaration(source: string, at: number): number {
  const subset = source.indexOf("[", at);
  const close = source.indexOf(">", at);
  if (close === -1) throw new XmlError("unterminated declaration", at);
  if (subset === -1 || subset > close) return close + 1;
  const subsetEnd = source.indexOf("]", subset);
  if (subsetEnd === -1) throw new XmlError("unterminated internal subset", at);
  return skipTo(source, subsetEnd, ">", "unterminated declaration");
}

/** Read an opening or self-closing tag, pushing it or handing it straight to `emit`. */
function openTag(
  source: string,
  start: number,
  stack: Building[],
  emit: (element: XmlElement) => void,
): number {
  let at = start + 1;
  const nameStart = at;
  while (at < source.length && NAME_CHARS.test(source[at] ?? "")) at += 1;
  if (at === nameStart) throw new XmlError("expected an element name", start);

  const node: Building = { name: source.slice(nameStart, at), attrs: {}, children: [], text: "" };

  while (at < source.length) {
    while (at < source.length && /\s/.test(source[at] ?? "")) at += 1;
    const here = source[at];

    if (here === ">") {
      stack.push(node);
      return at + 1;
    }
    if (here === "/") {
      if (source[at + 1] !== ">") throw new XmlError("expected />", at);
      emit(finish(node));
      return at + 2;
    }

    const keyStart = at;
    while (at < source.length && NAME_CHARS.test(source[at] ?? "")) at += 1;
    if (at === keyStart) throw new XmlError("expected an attribute name", at);
    const key = source.slice(keyStart, at);

    while (at < source.length && /\s/.test(source[at] ?? "")) at += 1;
    if (source[at] !== "=") throw new XmlError(`attribute ${key} has no value`, at);
    at += 1;
    while (at < source.length && /\s/.test(source[at] ?? "")) at += 1;

    const quote = source[at];
    if (quote !== '"' && quote !== "'") throw new XmlError(`attribute ${key} is unquoted`, at);
    const valueEnd = source.indexOf(quote, at + 1);
    if (valueEnd === -1) throw new XmlError(`attribute ${key} is unterminated`, at);
    node.attrs[key] = decodeEntities(source.slice(at + 1, valueEnd));
    at = valueEnd + 1;
  }

  throw new XmlError(`unterminated <${node.name}>`, start);
}
